<template>
  <div class="v2026-ap-anchor" @keydown.esc="onEsc" @focusout="onFocusOut">
    <!-- The pill wrapper in the owner draws the border/background/height; this button is a bare
         reset that reproduces the old <multiselect> trigger box-for-box. -->
    <button type="button"
            ref="triggerBtn"
            class="v2026-ap-trigger"
            aria-haspopup="listbox"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-controls="panelId"
            :title="selected ? selected.name : placeholder"
            @click.prevent="toggle"
            @keydown.down.prevent="onTriggerArrow('first')"
            @keydown.up.prevent="onTriggerArrow('last')">
      <span class="v2026-pill__lead" aria-hidden="true"
            :style="{ '-webkit-mask-image': 'url(' + leadIcon + ')', 'mask-image': 'url(' + leadIcon + ')' }"></span>
      <!-- 30px, not the 24 the old markup asked for: the global scss that upsized it is keyed to
           div[aria-owns="listbox-selectedAdmin"] and cannot match here. -->
      <img v-if="selected"
           class="v2026-ap-trigger__avatar"
           :src="selected.profile_picture || fallbackAvatar"
           @error="onAvatarError"
           alt="">
      <span class="v2026-ap-trigger__name">{{ triggerLabel }}</span>
      <span class="v2026-ap-trigger__chev" aria-hidden="true"><i class="fas fa-chevron-down"></i></span>
    </button>

    <!-- :renderKey is mandatory — the rows are method-driven state that does NOT flow through
         props, so Vue 2.7's stable-slot optimisation would freeze them. The scoped slot plus the
         :data-rk reference below is the other half of the same fix; three of four parts still
         leaves a frozen card. -->
    <OverlayCard :id="panelId"
                 class="v2026-ap-pos"
                 :open="open"
                 v-show="open"
                 :ignore="triggerEl"
                 :renderKey="renderKey"
                 role="region"
                 aria-label="เลือกแอดมินรับเคส"
                 @close="hide()">
      <template #default="ap">
        <div class="v2026-ap-search" :data-rk="ap.dirty">
          <i class="fas fa-search v2026-ap-search__ico" aria-hidden="true"></i>
          <input ref="searchInput"
                 v-model.trim="query"
                 type="text"
                 class="v2026-ap-search__input"
                 role="combobox"
                 aria-expanded="true"
                 aria-autocomplete="list"
                 aria-label="ค้นหา Admin"
                 placeholder="ค้นหา Admin"
                 :aria-controls="listId"
                 :aria-activedescendant="activeOptionId"
                 @keydown="onSearchKeydown">
        </div>

        <!-- NOT v-if="admins": a null list must show the empty state, never a blank card. -->
        <div :id="listId"
             ref="list"
             class="v2026-ap-list"
             role="listbox"
             tabindex="-1"
             aria-label="เลือกแอดมินรับเคส"
             @mousedown="onListMousedown">
          <!-- Selection AND the active cursor are baked into :key, so the row re-creates on change.
               Safe only because DOM focus never lives on a row — see onSearchKeydown. -->
          <div v-for="(a, i) in filteredAdmins"
               :key="String(a.id) + '|' + (isSelected(a) ? 1 : 0) + '|' + (i === activeIndex ? 1 : 0)"
               :id="optId(a)"
               class="v2026-ap-row"
               :class="{ 'is-locked': isSelected(a), 'is-active': i === activeIndex }"
               role="option"
               :aria-selected="isSelected(a) ? 'true' : 'false'"
               :aria-disabled="isSelected(a) ? 'true' : null"
               :aria-describedby="isSelected(a) ? hintId : null"
               :title="a.name"
               @click="onPick(a)">
            <img class="v2026-ap-row__avatar"
                 :src="a.profile_picture || fallbackAvatar"
                 @error="onAvatarError"
                 width="40" alt="">
            <span class="v2026-ap-row__name" lang="th">{{ a.name }}</span>
            <span v-if="isSelected(a)" class="v2026-ap-sr">รับเคสอยู่ ไม่สามารถลบออกได้</span>
            <i v-if="isSelected(a)" class="fas fa-lock v2026-ap-row__lock" aria-hidden="true"></i>
            <i v-if="isSelected(a)" class="fas fa-check v2026-ap-row__check" aria-hidden="true"></i>
          </div>

          <div v-if="!filteredAdmins.length" class="v2026-ap-empty">
            {{ query ? 'ไม่พบแอดมิน' : 'ยังไม่มีแอดมิน' }}
          </div>
        </div>

        <!-- The rule the old :deselectLabel only showed on hover. Wording is verbatim. -->
        <p v-if="selected" :id="hintId" class="v2026-ap-hint">
          <i class="fas fa-lock v2026-ap-hint__ico" aria-hidden="true"></i>
          <span>ไม่สามารถลบแอดมินออกได้ ต้องปิดเคสเท่านั้น</span>
        </p>
      </template>
    </OverlayCard>
  </div>
</template>

<script>
import OverlayCard from '@/components/coversation/OverlayCard'

const FALLBACK_AVATAR = require('@/assets/no_photos.png')

export default {
  name: 'admin-picker-v2026',
  components: {
    OverlayCard
  },
  props: {
    // Straight from Vuex, and fetched by the inbox toolbar rather than by this component:
    // it is null on first paint. Never call .length on it unguarded.
    admins: {
      type: Array,
      default: null
    },
    selected: {
      type: Object,
      default: null
    },
    leadIcon: {
      type: String,
      default: null
    },
    // A PRIMITIVE, so the watcher fires only on a real conversation change.
    conversationId: {
      type: [Number, String],
      default: null
    },
    placeholder: {
      type: String,
      default: 'แอดมินรับเคส'
    }
  },
  data () {
    return {
      open: false,
      query: '',
      // Cursor of the aria-activedescendant model. -1 = nothing active (empty list / no match).
      activeIndex: -1,
      // Handed to OverlayCard as :ignore, so clicking the trigger while open closes it once.
      triggerEl: null,
      fallbackAvatar: FALLBACK_AVATAR
    }
  },
  computed: {
    panelId () {
      return 'v2026-ap-card'
    },
    listId () {
      return 'v2026-ap-list'
    },
    hintId () {
      return 'v2026-ap-hint'
    },
    triggerLabel () {
      // null-safe: Admin.name is nullable, and the old singleLabel slot threw here.
      return this.selected ? (this.selected.name || '').slice(0, 10) : this.placeholder
    },
    filteredAdmins () {
      const q = (this.query || '').trim().toLowerCase()
      return (this.admins || []).filter(a => !q || (a.name || '').toLowerCase().includes(q))
    },
    activeOptionId () {
      const a = this.filteredAdmins[this.activeIndex]
      return a ? this.optId(a) : null
    },
    // Signature of EVERYTHING the slot renders. Anything left out of this string freezes on screen
    // behind Vue 2.7's stable-slot optimisation.
    renderKey () {
      return (this.selected ? String(this.selected.id) : 'none') +
        '#' + this.query +
        '#' + (this.open ? 1 : 0) +
        '#' + this.activeIndex +
        '#' + this.filteredAdmins.map(a => a.id).join(',')
    }
  },
  watch: {
    conversationId () {
      this.hide()
    },
    query () {
      // Guarded: hide() clears the query, and this watcher flushes a tick later — without the
      // guard it would resurrect an activeIndex on a card that is already closed.
      if (!this.open) return
      this.activeIndex = this.filteredAdmins.length ? 0 : -1
      this.$nextTick(() => {
        if (this.$refs.list) this.$refs.list.scrollTop = 0
      })
    }
  },
  mounted () {
    this.triggerEl = this.$refs.triggerBtn || null
  },
  methods: {
    optId (a) {
      return this.panelId + '-opt-' + a.id
    },
    isSelected (a) {
      // by id, not by name: Admin.name has no unique index and can be null
      return !!this.selected && String(this.selected.id) === String(a.id)
    },
    // --- public API ------------------------------------------------------------------------
    show () {
      if (this.open) return
      this.query = ''
      this.open = true
      this.$emit('open')
      this.activeIndex = this.initialActiveIndex()
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus()
        this.scrollActiveIntoView()
      })
    },
    hide (returnFocus = false) {
      this.open = false
      this.query = ''
      this.activeIndex = -1
      if (returnFocus) {
        this.$nextTick(() => {
          // The node can be gone by now: assigning can re-render the whole panel.
          if (this.$refs.triggerBtn) this.$refs.triggerBtn.focus()
        })
      }
    },
    toggle () {
      if (this.open) this.hide(true)
      else this.show()
    },
    // --- keyboard / focus ------------------------------------------------------------------
    initialActiveIndex () {
      const rows = this.filteredAdmins
      if (!rows.length) return -1
      const i = rows.findIndex(a => this.isSelected(a))
      return i === -1 ? 0 : i
    },
    scrollActiveIntoView () {
      const list = this.$refs.list
      if (!list || this.activeIndex < 0) return
      // The row nodes are re-created on every arrow press (activeIndex is in the :key), so the
      // node is re-queried here and never held across a keypress.
      const el = list.children[this.activeIndex]
      if (el) el.scrollIntoView({ block: 'nearest' })
    },
    moveTo (index) {
      const rows = this.filteredAdmins
      if (!rows.length) {
        this.activeIndex = -1
        return
      }
      this.activeIndex = Math.max(0, Math.min(rows.length - 1, index))
      this.$nextTick(() => this.scrollActiveIntoView())
    },
    onTriggerArrow (where) {
      const target = () => this.moveTo(where === 'last' ? this.filteredAdmins.length - 1 : 0)
      if (!this.open) {
        this.show()
        this.$nextTick(target)
        return
      }
      target()
    },
    onSearchKeydown (e) {
      const key = e.key
      if (key === 'ArrowDown') {
        e.preventDefault()
        this.moveTo(this.activeIndex + 1)
      } else if (key === 'ArrowUp') {
        e.preventDefault()
        this.moveTo(this.activeIndex - 1)
      } else if (key === 'Home' || key === 'End') {
        // With text typed these belong to the caret; hijacking them would steal a real action.
        if (this.query) return
        e.preventDefault()
        this.moveTo(key === 'Home' ? 0 : this.filteredAdmins.length - 1)
      } else if (key === 'Enter') {
        e.preventDefault()
        const a = this.filteredAdmins[this.activeIndex]
        if (a) this.onPick(a)
      }
    },
    onListMousedown (e) {
      const list = this.$refs.list
      if (!list) return
      // a press on the scrollbar gutter must stay draggable
      if (e.target === list && e.offsetX >= list.clientWidth) return
      // everything else keeps the caret in the search input
      e.preventDefault()
    },
    onEsc (e) {
      if (!this.open) return
      e.stopPropagation()
      this.hide(true)
    },
    onFocusOut (e) {
      if (!this.open) return
      // relatedTarget is null when the WINDOW loses focus (Alt-Tab) or a non-focusable area is
      // clicked. Closing there would make the card vanish on Alt-Tab; the real outside-click case
      // is already covered by OverlayCard's document mousedown listener.
      if (!e.relatedTarget) return
      if (this.$el.contains(e.relatedTarget)) return
      this.hide()
    },
    // --- selection -------------------------------------------------------------------------
    onPick (a) {
      // A second activation before the close lands would produce a second assign PUT.
      if (!this.open) return
      if (this.isSelected(a)) {
        // The locked row: no emit at all. An admin can only be removed by closing the case.
        this.hide(true)
        return
      }
      // Close BEFORE the emit: the owner's handler writes and fires the PUT synchronously.
      this.hide(true)
      this.$emit('select', a)
    },
    onAvatarError (e) {
      const img = e.target
      // getAttribute, not .src: the property returns an absolute URL and would never compare
      // equal, so a failing fallback would re-enter @error forever.
      if (img.getAttribute('src') === FALLBACK_AVATAR) return
      img.src = FALLBACK_AVATAR
    }
  }
}
</script>

<style scoped>
.v2026-ap-anchor {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.v2026-ap-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0;
  height: var(--v2026-ctrl-h, 36px);
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

/* Re-declared, NOT inherited: the owner's .v2026-pill__lead rule carries the owner's data-v-*
   attribute and cannot reach a child component's internals. */
.v2026-pill__lead {
  width: 16px;
  height: 16px;
  display: inline-block;
  flex-shrink: 0;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-icon, #475467);
}

/* 11px = 4px pill gap + 2px .multiselect__tags padding-left + 5px .multiselect__single
   padding-left; 8px = bootstrap .mr-2. 30px because the global scss that upsized the old
   24px avatar cannot match this markup. */
.v2026-ap-trigger__avatar {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
  margin-left: 11px;
  margin-right: 8px;
}

.v2026-ap-trigger__name {
  min-width: 0;
  white-space: nowrap;
  /* NO overflow:hidden. The old label was not clipped either, and a clip box this short eats the
     top of Thai tone marks. The label is bounded by slice(0, 10) instead. */
  line-height: 1;
  font-size: var(--v2026-fs-ctrl, 13px);
  color: var(--v2026-text-secondary, #667085);
  margin-left: 6px;
}

/* The assigned name used .text-small (92%); the placeholder did not. line-height goes back to
   normal because .text-small was an INLINE span, whose box is the font's content area, not the
   line-height — keeping 1 here renders the glyphs 1px high. */
.v2026-ap-trigger__avatar + .v2026-ap-trigger__name {
  font-size: 92%;
  line-height: normal;
  margin-left: 0;
}

.v2026-ap-trigger__chev {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  margin: 0 4px;
  font-size: 10px;
  line-height: 1;
  color: var(--v2026-text-muted, #98A2B3);
}

/* Written with the .v2026-ap-anchor ancestor on purpose: it must out-SPECIFY OverlayCard's own
   min-width/padding rather than rely on style-injection order. */
.v2026-ap-anchor .v2026-ap-pos {
  width: 280px;
  top: calc(100% + var(--v2026-sp-2, 8px) + 1px);
  left: -9px;
  min-width: 0;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 160px);
  display: flex;
  flex-direction: column;
}

.v2026-ap-search {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
  background: #F8FAFC;
  border: 1px solid var(--v2026-border, #E4E7EC);
  border-radius: 10px;
}

.v2026-ap-search:focus-within {
  border-color: var(--v2026-color-main, #31C1D7);
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-ap-search__ico {
  flex: 0 0 auto;
  font-size: 14px;
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-ap-search__input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: #101828;
  font-size: 14px;
}

.v2026-ap-search__input::placeholder {
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-ap-list {
  /* min-height:0 is mandatory: without it the flex child refuses to shrink and pushes the
     pinned hint out of the card. */
  flex: 1 1 auto;
  min-height: 0;
  max-height: 260px;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 4px;
  outline: none;
}

.v2026-ap-list::-webkit-scrollbar {
  width: 6px;
}

.v2026-ap-list::-webkit-scrollbar-thumb {
  background: var(--v2026-color-main, #31C1D7);
  border-radius: 3px;
}

.v2026-ap-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 14px;
  background: #ffffff;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.v2026-ap-row:hover {
  background: var(--v2026-surface-hover, #E9ECF1);
}

/* the current assignee: current and fixed, NOT disabled-looking — it must stay legible */
.v2026-ap-row.is-locked,
.v2026-ap-row.is-locked:hover {
  background: var(--v2026-color-main-soft, #31C1D714);
  font-weight: 600;
  cursor: default;
  box-shadow: inset 0 0 0 1px rgba(49, 193, 215, 0.35);
}

/* hover changes the fill, active draws a ring — they may co-occur and stay distinguishable */
.v2026-ap-row.is-active {
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-ap-row.is-locked.is-active {
  box-shadow: inset 0 0 0 1px rgba(49, 193, 215, 0.35), 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-ap-row__avatar {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.v2026-ap-row__name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v2026-ap-row__lock {
  flex: 0 0 auto;
  font-size: 11px;
  color: var(--v2026-text-secondary, #667085);
}

.v2026-ap-row__check {
  flex: 0 0 auto;
  font-size: 13px;
  color: var(--v2026-color-main, #31C1D7);
}

.v2026-ap-empty {
  padding: 20px 12px;
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 12px;
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-ap-hint {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: var(--v2026-sp-2, 8px) 0 0;
  padding: var(--v2026-sp-2, 8px) 2px 0;
  border-top: 1px solid var(--v2026-border, #E4E7EC);
  font-size: 12px;
  line-height: 16px;
  color: var(--v2026-text-secondary, #667085);
}

.v2026-ap-hint__ico {
  flex: 0 0 auto;
  font-size: 11px;
  line-height: 16px;
}

.v2026-ap-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .v2026-ap-row,
  .v2026-ap-trigger__caret {
    transition: none;
  }
}
</style>
